import { Button, Card, Dropdown, DropdownButton, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import React, { SetStateAction } from 'react';
import { ICampaignReview } from '../../../model/campaign/ICampaignReview';
import { DashboardService } from '../DashboardService';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { formatDate } from '../../../utils/Functions';
import { FiInfo } from 'react-icons/fi';
import { priceSplitter } from '../../../utils/helpers/NumberFormater';
import { useDispatch } from 'react-redux';
import generalSlice from '../../../store/general/GeneralSlice';

import { getTruncatedData } from '../../../utils/helpers/getTruncatedData';
import TooltipOverlay from '../../../shared/components/TooltipOverlay';
import TableSkeleton, { ITableColumn } from '../../../shared/components/TableSkeleton';

const REVIEW_COLUMNS: ITableColumn[] = [
    { label: 'Name' },
    { label: 'Date' },
    { label: 'Brand' },
    { label: 'Email' },
    { label: 'Budget' },
    { label: 'Status' },
    { label: 'Type' },
    { label: 'Usage rights' },
    { label: 'Actions' },
];

const SKELETON_ROWS = 5;

export interface InCampaignReview {
    campaignReview: ICampaignReview[];
    setTriggerUpdate: React.Dispatch<SetStateAction<boolean>>;
    isLoading?: boolean;
}

export function CampaignReview({ campaignReview, setTriggerUpdate, isLoading = false }: InCampaignReview) {
    const dispatch = useDispatch();
    const handleSubmit = (campaignId: number, status: string) => {
        DashboardService.campaignApprovalAction(campaignId, {
            approvalStatus: status,
        })
            .then(() => {
                setTriggerUpdate(prev => !prev);
                SuccessToast('Success');
                dispatch(
                    generalSlice.actions.setConfirmModal({
                        show: false,
                    })
                );
            })
            .catch(error => ErrorToast(error));
    };

    return (
        <Card className="p-4 mt-3" style={{ maxHeight: '500px', overflow: 'auto' }}>
            <h5 className="p-3">Campaigns for review{isLoading ? '' : `: ${campaignReview?.length ?? 0}`}</h5>

            {isLoading ? (
                <TableSkeleton columns={REVIEW_COLUMNS} rows={SKELETON_ROWS} />
            ) : (
                <Table hover responsive>
                    <thead>
                        <tr>
                            {REVIEW_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {campaignReview
                            ?.sort((a, b) => b.id - a.id)
                            ?.map(campaign => {
                                const { truncatedName: truncatedTitle, isTruncated: isTitleTruncated } =
                                    getTruncatedData(campaign?.title ?? '', 25);
                                return (
                                    <tr key={campaign?.id}>
                                        <td>
                                            <TooltipOverlay content={isTitleTruncated ? campaign?.title : ''}>
                                                <span>{truncatedTitle || '-'}</span>
                                            </TooltipOverlay>
                                        </td>
                                        <td>{formatDate(campaign.publishedAt)}</td>
                                        <td>
                                            {campaign?.company?.profilePhoto?.thumbnail ? (
                                                <img
                                                    alt=""
                                                    className="object-cover rounded"
                                                    src={campaign?.company?.profilePhoto?.thumbnail}
                                                    style={{ width: 35, height: 35 }}
                                                />
                                            ) : (
                                                <img
                                                    alt=""
                                                    src="/images/placeholder.png"
                                                    style={{ width: 35, height: 35 }}
                                                />
                                            )}
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id="impressions-tooltip">
                                                        {campaign?.company?.displayName}
                                                    </Tooltip>
                                                }
                                            >
                                                <span className="ml-1">
                                                    <FiInfo size={14} className="cursor-pointer" />
                                                </span>
                                            </OverlayTrigger>
                                        </td>
                                        <td>
                                            <ImpersonateEye id={campaign.user?.id ?? 0} />
                                            {campaign?.user?.email}
                                        </td>
                                        <td>{priceSplitter(campaign?.grossBudget)} €</td>
                                        <td>
                                            <span className={`status ${campaign?.status}`}>
                                                {campaign?.status === 'published' ? 'public' : campaign.status}
                                            </span>
                                        </td>
                                        <td>
                                            <span
                                                className={`status ${
                                                    campaign.isLongTerm
                                                        ? 'long'
                                                        : campaign.parent
                                                        ? 'linked'
                                                        : 'standard'
                                                }`}
                                            >
                                                {campaign.isLongTerm ? 'Long' : campaign.parent ? 'Linked' : 'Standard'}
                                            </span>
                                        </td>
                                        <td>
                                            {campaign?.brandedContent ||
                                            campaign?.hasByLaws ||
                                            campaign?.hasExclusivity ||
                                            campaign?.usageRights
                                                ? 'Yes'
                                                : 'No'}
                                        </td>
                                        <td>
                                            <DropdownButton title="Actions">
                                                <Dropdown.Item
                                                    eventKey="1"
                                                    onClick={() => {
                                                        dispatch(
                                                            generalSlice.actions.setConfirmModal({
                                                                show: true,
                                                                title: 'Campaign communication',
                                                                contentBody: (
                                                                    <div>
                                                                        Are you sure that you want to approve this
                                                                        campaign?
                                                                    </div>
                                                                ),
                                                                action: () => handleSubmit(campaign?.id, 'approved'),
                                                            })
                                                        );
                                                    }}
                                                >
                                                    Approve
                                                </Dropdown.Item>
                                                <Dropdown.Item
                                                    eventKey="2"
                                                    className="text-danger"
                                                    onClick={() => {
                                                        dispatch(
                                                            generalSlice.actions.setConfirmModal({
                                                                show: true,
                                                                title: 'Campaign communication',
                                                                contentBody: (
                                                                    <div>
                                                                        Are you sure that you want to decline this
                                                                        campaign?
                                                                    </div>
                                                                ),
                                                                action: () => handleSubmit(campaign?.id, 'rejected'),
                                                            })
                                                        );
                                                    }}
                                                >
                                                    Decline
                                                </Dropdown.Item>
                                            </DropdownButton>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                    <div
                        className={campaignReview.length === 1 ? 'h-60px' : campaignReview.length === 2 ? 'h-20px' : ''}
                    />
                </Table>
            )}
        </Card>
    );
}
